import { fetchAPI } from '@/api/fetch';
import { apiSignUp, apiSendVerificationCode } from '@/api/services';
import useAuth from '@/store/auth';
import { SignUpStep, useSignUp } from '@/store/signup';
import { AgencyPlan } from '@/types/enums';
import { Box, Button, Flex, Input, Spinner, Stack, Text, Center } from '@chakra-ui/react';
import { CaretLeft } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function EmailVerification() {
  const signUpStore = useSignUp();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string[]>(Array(6).fill(''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cooldownTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Focus on the first input when component mounts
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        window.clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const startCooldown = () => {
    setCooldownTime(180); // 3 minutes in seconds
    if (cooldownTimerRef.current) {
      window.clearInterval(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = window.setInterval(() => {
      setCooldownTime((prev) => {
        if (prev <= 1) {
          window.clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (index: number, value: string) => {
    setSubmitError(null);

    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      return;
    }

    if (value.length > 1) {
      value = value.slice(0, 1);
    }

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are filled
    if (value && index === 5) {
      const isComplete = newCode.every((digit) => digit !== '');
      if (isComplete) {
        onSubmit(newCode.join(''));
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^\d]/g, '').slice(0, 6);
    const newCode = [...verificationCode];

    for (let i = 0; i < pastedData.length; i++) {
      if (i < 6) {
        newCode[i] = pastedData[i];
      }
    }

    setVerificationCode(newCode);
    if (pastedData.length < 6) {
      inputRefs.current[pastedData.length]?.focus();
    } else {
      onSubmit(newCode.join(''));
    }
  };

  const onSubmit = async (code: string) => {
    setIsSubmitting(true);
    setSubmitError(null);
    if (code.length !== 6) {
      setSubmitError('Please enter the complete verification code');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetchAPI({
        request: () =>
          apiSignUp({
            email: signUpStore.email,
            password: signUpStore.password,
            name: signUpStore.name,
            agency_name: signUpStore.agencyName,
            plan: AgencyPlan.TRIAL,
            verification_code: code
          })
      });

      if (response.success) {
        const { account, access_token: accessToken, refresh_token: refreshToken } = response.data;
        login(account, accessToken, refreshToken);
        signUpStore.reset();
        navigate('/');
      } else {
        setSubmitError(response.message ?? 'Something went wrong. Please try again.');
        console.error(response.message);
      }
    } catch (error) {
      setSubmitError('Something went wrong. Please try again.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (!isSubmitting) {
      signUpStore.setStep(SignUpStep.CreateAccount);
    }
  };

  const handleResend = async () => {
    if (cooldownTime > 0) return;

    setIsResending(true);
    setSubmitError(null);
    setResendSuccess(false);

    try {
      const response = await fetchAPI({
        request: () =>
          apiSendVerificationCode({
            email: signUpStore.email,
            name: signUpStore.name
          })
      });

      if (response.success) {
        setResendSuccess(true);
        startCooldown();
        // Clear success message after 5 seconds
        setTimeout(() => {
          setResendSuccess(false);
        }, 5000);
      } else {
        setSubmitError(response.message ?? 'Failed to resend verification code. Please try again.');
        console.error(response.message);
      }
    } catch (error) {
      setSubmitError('Failed to resend verification code. Please try again.');
      console.error(error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Stack w="100%" h="100%" overflowY="auto" position="relative">
      {isSubmitting && (
        <Center
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(255, 255, 255, 0.8)"
          zIndex={1}
        >
          <Stack gap={4} alignItems="center">
            <Spinner size="xl" color="primary.900" borderWidth="3px" />
            <Text color="neutrals.900" fontWeight={500}>
              Verifying your email...
            </Text>
          </Stack>
        </Center>
      )}
      <Box p="12px 50px">
        <Flex justifyContent="left" alignItems="center" gap="16px">
          <CaretLeft
            weight="bold"
            size={28}
            color="neutrals.900"
            onClick={handleBack}
            cursor={isSubmitting ? 'not-allowed' : 'pointer'}
          />
          <Text fontSize="32px" fontWeight={700}>
            Email Verification
          </Text>
        </Flex>

        <Text fontSize="14px" color="neutrals.500" mt="100px" textAlign="center" mb="20px">
          Please enter the 6-digit verification code sent to{' '}
          <Text as="span" fontWeight={500} color="neutrals.900">
            {signUpStore.email}
          </Text>
        </Text>

        <Flex gap="8px" mb="16px" justifyContent="center">
          {verificationCode.map((digit, index) => (
            <Input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              maxLength={1}
              width="48px"
              height="48px"
              textAlign="center"
              fontSize="24px"
              fontWeight="600"
              borderRadius="8px"
              borderColor={submitError ? 'error.700' : 'neutrals.300'}
              _focus={{
                borderColor: 'primary.900',
                boxShadow: 'none'
              }}
              _hover={{
                borderColor: 'primary.900'
              }}
              disabled={isSubmitting}
            />
          ))}
        </Flex>

        {submitError && (
          <Text fontSize="14px" color="error.700" textAlign="center" mb="16px">
            {submitError}
          </Text>
        )}

        <Flex justifyContent="center" alignItems="center">
          <Text fontSize="14px" color="neutrals.500" mr="8px">
            Didn't receive the code?
          </Text>
          <Button
            variant="ghost"
            p="0"
            fontSize="14px"
            fontWeight="500"
            color={cooldownTime > 0 ? 'neutrals.500' : 'primary.900'}
            _hover={{
              bg: 'neutrals.0',
              textDecoration: cooldownTime > 0 ? 'none' : 'underline'
            }}
            cursor={cooldownTime > 0 ? 'not-allowed' : 'pointer'}
            disabled={isResending || cooldownTime > 0}
            onClick={handleResend}
          >
            Resend {cooldownTime > 0 && `(${formatTime(cooldownTime)})`}
          </Button>
          {isResending && <Spinner size="sm" ml="8px" />}
        </Flex>
        {resendSuccess && (
          <Text fontSize="14px" color="success.700" textAlign="center" mt="8px">
            Code resent successfully!
          </Text>
        )}
      </Box>
    </Stack>
  );
}
